// ════════════════════════════════════════════════════════════════════════
//  Das Fortschrittsband – eine Zelle je Lektion
//
//  Zehn Lektionen stehen in einer Spalte statt in zehn Spalten, dreißig in zwei
//  Reihen. Das ist der Unterschied, der die Ergebnisseite auf eine Zeile je Kind
//  bringt.
//
//  Zwei Dimensionen, zwei Mittel: die Sterne machen die Farbe, die
//  Vollständigkeit die Form. Beides in Farbe unterzubringen hieße vier Töne in
//  einer Zelle von acht Pixeln.
// ════════════════════════════════════════════════════════════════════════

import type { BandCell } from "../lib/overview.js";
import own from "./ProgressBand.module.css";

const starWord = (stars: number) => (stars === 1 ? "ein Stern" : `${stars} Sterne`);

/**
 * Was diese Zelle sagt – für den title und für den Screenreader.
 *
 * Sie sagt es vollständig, mit den Stufen: über der Tabelle stand einmal eine
 * Legende, die Farbe und halbe Höhe erklärte. Eine Legende erklärt ein Bild
 * neben dem Bild; hier steht die Erklärung an dem Feld, um das es geht, und
 * kostet keine Zeile auf der Seite.
 */
function describe(cell: BandCell): string {
  const name = `Lektion ${cell.number}: ${cell.title}`;
  const stufen = `${cell.stagesDone} von ${cell.stagesTotal} Stufen geübt`;
  if (cell.mark === null) return `${name} – noch nicht begonnen`;
  const stars = cell.mark.stars === null ? "keine Sterne" : starWord(cell.mark.stars);
  return cell.mark.complete
    ? `${name} – ${stufen}, ${stars}`
    : `${name} – ${stufen}, ${stars}, noch nicht alle`;
}

export function ProgressBand({
  lessons,
  label,
}: {
  lessons: readonly BandCell[];
  /** Was das Band als Ganzes zeigt, z. B. „Grundkurs 1, 14 von 30 Stufen“. */
  label: string;
}) {
  return (
    <div className={own.band} role="img" aria-label={label}>
      {lessons.map((cell) => (
        <span
          key={cell.key}
          className={[
            own.cell,
            cell.mark?.stars ? own[`stars${cell.mark.stars}`] : "",
            cell.mark && !cell.mark.complete ? own.partial : "",
          ]
            .filter(Boolean)
            .join(" ")}
          // Auf dem iPad gibt es kein Hover – deshalb steht dieselbe Auskunft im
          // Kindblick als Text. Der title ist die Bequemlichkeit am Schreibtisch,
          // nicht der einzige Weg zur Information.
          title={describe(cell)}
        />
      ))}
    </div>
  );
}
